<template>
  <div class="skills-editor" data-testid="skillsEditor-container">
    <v-combobox
      v-model="selectedSkills"
      data-private
      data-testid="skillsEditor-combobox-skill"
      dense
      hide-no-data
      multiple
      outlined
      ref="skillCombobox"
      :autofocus="autofocus"
      :counter="counter"
      :disabled="isLoading"
      :errorMessages="errorMessages"
      :hint="fieldHint"
      :items="[]"
      :label="fieldLabel"
      :loading="isLoading"
      :maxlength="$constants.profile.skillsMaxLength"
      :persistentHint="hasPendingText"
      :placeholder="$t('profile.skills_placeholder')"
      @change="onChange"
      @update:search-input="onSearchInput"
    >
      <template v-slot:selection="{ item }">
        <v-chip
          class="mt-1"
          close
          close-icon="mdi-close"
          data-testid="skillsEditor-chip-skill"
          outlined
          small
          :ripple="false"
          @click:close="removeSkill(item)"
        >
          {{ item }}
        </v-chip>
      </template>
    </v-combobox>
  </div>
</template>

<script>
const NO_LIMIT = 0

const REJECTION_REASON = Object.freeze({
  DUPLICATED: 'duplicated',
  MAX_REACHED: 'maxReached',
})

export default {
  name: 'SkillsEditor',

  props: {
    skills: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxSkills: {
      type: Number,
      required: false,
      default: NO_LIMIT,
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      hasPendingText: false,
      hasPendingUpdate: false,
      rejection: null,
      selectedSkills: [...this.skills],
    }
  },

  watch: {
    skills(newSkills) {
      this.selectedSkills = [...newSkills]
    },

    isLoading(next, prev) {
      if (!prev || next || !this.hasPendingUpdate) return

      this.hasPendingUpdate = false
      this.focusInput()
    },
  },

  computed: {
    fieldHint() {
      if (this.hasPendingText) {
        return this.$t('profile_settings.skills_pending_hint')
      }

      return this.$t('profile_settings.skills_hint')
    },

    fieldLabel() {
      return this.label || `${this.$t('profile_settings.skill_one')}*`
    },

    counter() {
      return this.maxSkills === NO_LIMIT ? false : this.maxSkills
    },

    // Accounts edited from their own profile have no cap, so an account can
    // already hold more skills than an admin is allowed to add. Those skills
    // are kept instead of truncated, and only additions past the cap are
    // rejected.
    acceptedSkillsLimit() {
      if (this.maxSkills === NO_LIMIT) return Infinity

      return Math.max(this.maxSkills, this.skills.length)
    },

    errorMessages() {
      if (this.rejection?.reason === REJECTION_REASON.MAX_REACHED) {
        return [
          this.$t('profile_settings.skills_max_reached', {
            max: this.maxSkills,
          }),
        ]
      }

      if (this.rejection?.reason === REJECTION_REASON.DUPLICATED) {
        return [
          this.$t('profile_settings.skill_is_duplicated', {
            skill: this.rejection.skill,
          }),
        ]
      }

      return []
    },
  },

  methods: {
    onChange(skills) {
      const accepted = this.getAcceptedSkills(skills)

      this.selectedSkills = accepted
      this.hasPendingUpdate = true
      this.$emit('update', { skills: accepted })
    },

    getAcceptedSkills(skills) {
      const accepted = []
      const seen = new Set()
      let rejection = null

      skills.forEach(skill => {
        const trimmed = skill?.trim?.() ?? ''

        if (!trimmed) return

        if (seen.has(trimmed.toLowerCase())) {
          rejection = { reason: REJECTION_REASON.DUPLICATED, skill: trimmed }
          return
        }

        if (accepted.length >= this.acceptedSkillsLimit) {
          rejection = { reason: REJECTION_REASON.MAX_REACHED }
          return
        }

        seen.add(trimmed.toLowerCase())
        accepted.push(trimmed)
      })

      this.rejection = rejection

      return accepted
    },

    removeSkill(skillToRemove) {
      this.onChange(
        this.selectedSkills.filter(skill => skill !== skillToRemove)
      )
    },

    clearRejection() {
      this.rejection = null
    },

    onSearchInput(value) {
      this.updatePendingState(value)
      this.clearRejection()
    },

    updatePendingState(value) {
      const hasPendingText = Boolean(value?.trim?.())

      if (hasPendingText === this.hasPendingText) return

      this.hasPendingText = hasPendingText
      this.$emit('update:hasPendingText', hasPendingText)
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.skillCombobox?.$refs?.input?.focus()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.skills-editor {
  ::v-deep .v-select__selections {
    max-height: 120px;
    overflow-y: auto;
  }

  ::v-deep .v-chip__close {
    color: $color-blue-grey-darken1;
  }
}
</style>
